// frontend/src/pages/technician/TicketDetails.js

import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Paper,
    Alert,
    CircularProgress,
    Chip,
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemAvatar,
    TextField,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Fab
} from '@mui/material';
// Importar nuestro Timeline personalizado
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineOppositeContent
} from '../../components/CustomTimeline';
import {
    ArrowBack,
    Build,
    Person,
    Phone,
    Email,
    LocationOn,
    Router,
    SignalWifi4Bar,
    Schedule,
    Message,
    Send,
    CheckCircle,
    PlayArrow,
    Stop,
    Navigation,
    ExpandMore,
    Assignment,
    Warning,
    Info,
    Error as ErrorIcon,
    Refresh
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { technicianApiService } from '../../services/technicianApiService';

const TicketDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [ticket, setTicket] = useState(null);
    const [responses, setResponses] = useState([]);
    const [connectivityInfo, setConnectivityInfo] = useState([]);
    const [loading, setLoading] = useState(true);
    const [responseDialog, setResponseDialog] = useState(false);
    const [resolveDialog, setResolveDialog] = useState(false);
    const [responseText, setResponseText] = useState('');
    const [resolutionText, setResolutionText] = useState('');
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadTicketDetails();
    }, [id]);

    const loadTicketDetails = async () => {
        try {
            setLoading(true);
            const data = await technicianApiService.getTicketDetails(id);
            setTicket(data.ticket);
            setResponses(data.responses);
            setConnectivityInfo(data.connectivity_info || []);
            setError(null);
        } catch (error) {
            console.error('Error cargando detalles del ticket:', error);
            setError('Error cargando detalles del ticket');
        } finally {
            setLoading(false);
        }
    };

    const updateTicketStatus = async (newStatus) => {
        try {
            await technicianApiService.updateTicketStatus(id, newStatus);
            await loadTicketDetails();
        } catch (error) {
            console.error('Error actualizando estado:', error);
            setError('Error actualizando estado del ticket');
        }
    };

    const addResponse = async () => {
        if (!responseText.trim()) return;

        try {
            setSubmitting(true);
            await technicianApiService.addTicketResponse(id, {
                content: responseText,
                change_status: ticket.status === 'open' ? 'in_progress' : null
            });

            setResponseText('');
            setResponseDialog(false);
            await loadTicketDetails();
        } catch (error) {
            console.error('Error agregando respuesta:', error);
            setError('Error agregando respuesta');
        } finally {
            setSubmitting(false);
        }
    };

    const resolveTicket = async () => {
        if (!resolutionText.trim()) return;

        try {
            setSubmitting(true);
            await technicianApiService.resolveTicket(id, {
                resolution: resolutionText
            });

            setResolutionText('');
            setResolveDialog(false);
            navigate('/technician/tickets', {
                state: { message: 'Ticket resuelto exitosamente' }
            });
        } catch (error) {
            console.error('Error resolviendo ticket:', error);
            setError('Error resolviendo ticket');
        } finally {
            setSubmitting(false);
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'urgent': return 'error';
            case 'high': return 'warning';
            case 'medium': return 'info';
            case 'low': return 'success';
            default: return 'default';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'open': return 'error';
            case 'in_progress': return 'warning';
            case 'closed': return 'success';
            default: return 'default';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'open': return 'Abierto';
            case 'in_progress': return 'En Progreso';
            case 'closed': return 'Cerrado';
            default: return status;
        }
    };

    const getPriorityText = (priority) => {
        switch (priority) {
            case 'urgent': return 'Urgente';
            case 'high': return 'Alta';
            case 'medium': return 'Media';
            case 'low': return 'Baja';
            default: return priority;
        }
    };

    const makePhoneCall = (phoneNumber) => {
        window.open(`tel:${phoneNumber}`);
    };

    const openNavigation = (coordinates) => {
        if (coordinates) {
            const coords = coordinates.replace(/[()]/g, '').split(',');
            if (coords.length === 2) {
                const val1 = parseFloat(coords[0].trim());
                const val2 = parseFloat(coords[1].trim());
                let lat = val1;
                let lng = val2;
                if (Math.abs(val1) > 90 && Math.abs(val2) <= 90) {
                    lat = val2;
                    lng = val1;
                }
                window.open(`https://maps.google.com/maps?q=${lat},${lng}`, '_blank');
            }
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (!ticket) {
        return (
            <Alert severity="error">
                Ticket no encontrado
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={2}>
                        <IconButton onClick={() => navigate('/technician/tickets')}>
                            <ArrowBack />
                        </IconButton>
                        <Box>
                            <Typography variant="h5">
                                {ticket.subject}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Ticket #{ticket.id.slice(0, 8)} - {ticket.first_name} {ticket.last_name}
                            </Typography>
                        </Box>
                    </Box>
                    <Box display="flex" gap={1}>
                        <Chip
                            label={getPriorityText(ticket.priority)}
                            color={getPriorityColor(ticket.priority)}
                        />
                        <Chip
                            label={getStatusText(ticket.status)}
                            color={getStatusColor(ticket.status)}
                        />
                    </Box>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Información principal */}
                <Grid item xs={12} md={8}>
                    {/* Descripción del problema */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Descripción del Problema
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Typography variant="body1">
                                    {ticket.description}
                                </Typography>
                            </Paper>

                            <Box mt={2} display="flex" gap={1}>
                                <Chip label={`Categoría: ${ticket.category}`} variant="outlined" />
                                <Chip
                                    label={`Creado: ${new Date(ticket.created_at).toLocaleDateString('es-PE')}`}
                                    variant="outlined"
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Historial de conversación */}
                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6">
                                    Historial de Conversación ({responses.length})
                                </Typography>
                                <IconButton onClick={loadTicketDetails}>
                                    <Refresh />
                                </IconButton>
                            </Box>

                            {responses.length > 0 ? (
                                <Timeline>
                                    {responses.map((response, index) => (
                                        <TimelineItem key={response.id}>
                                            <TimelineOppositeContent color="textSecondary">
                                                {new Date(response.created_at).toLocaleString('es-PE')}
                                            </TimelineOppositeContent>
                                            <TimelineSeparator>
                                                <TimelineDot color={response.response_type === 'client' ? 'primary' : 'success'}>
                                                    {response.response_type === 'client' ? <Person /> : <Build />}
                                                </TimelineDot>
                                                <TimelineConnector />
                                            </TimelineSeparator>
                                            <TimelineContent>
                                                <Paper sx={{
                                                    p: 2,
                                                    bgcolor: response.response_type === 'client' ? 'primary.50' : 'success.50',
                                                    border: '1px solid',
                                                    borderColor: response.response_type === 'client' ? 'primary.100' : 'success.100'
                                                }}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        {response.response_by_name} ({response.response_by_type})
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                        {response.content}
                                                    </Typography>
                                                </Paper>
                                            </TimelineContent>
                                        </TimelineItem>
                                    ))}
                                </Timeline>
                            ) : (
                                <Alert severity="info">
                                    Aún no hay respuestas en este ticket
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Panel lateral */}
                <Grid item xs={12} md={4}>
                    {/* Información del cliente */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Información del Cliente
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><Person /></ListItemIcon>
                                    <ListItemText
                                        primary="Cliente"
                                        secondary={`${ticket.first_name} ${ticket.last_name}`}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Phone /></ListItemIcon>
                                    <ListItemText
                                        primary="Teléfono"
                                        secondary={ticket.phone}
                                    />
                                    <IconButton
                                        edge="end"
                                        onClick={() => makePhoneCall(ticket.phone)}
                                    >
                                        <Phone />
                                    </IconButton>
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Email /></ListItemIcon>
                                    <ListItemText
                                        primary="Email"
                                        secondary={ticket.email}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><LocationOn /></ListItemIcon>
                                    <ListItemText
                                        primary="Dirección"
                                        secondary={ticket.address}
                                    />
                                    <IconButton
                                        edge="end"
                                        onClick={() => openNavigation(ticket.coordinates)}
                                        disabled={!ticket.coordinates}
                                    >
                                        <Navigation />
                                    </IconButton>
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>

                    {/* Información técnica */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Información Técnica
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><SignalWifi4Bar /></ListItemIcon>
                                    <ListItemText
                                        primary="Plan"
                                        secondary={ticket.plan_name}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><Router /></ListItemIcon>
                                    <ListItemText
                                        primary="IP del Cliente"
                                        secondary={ticket.ip_address}
                                    />
                                </ListItem>
                                {ticket.router_name && (
                                    <ListItem>
                                        <ListItemIcon><Router /></ListItemIcon>
                                        <ListItemText
                                            primary="Router"
                                            secondary={ticket.router_name}
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </CardContent>
                    </Card>

                    {/* Estado de conexión */}
                    {connectivityInfo.length > 0 && (
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="h6">Estado de Conexión</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <List dense>
                                    {connectivityInfo.slice(0, 5).map((info, index) => (
                                        <ListItem key={index}>
                                            <ListItemText
                                                primary={`${new Date(info.timestamp).toLocaleString('es-PE')}`}
                                                secondary={`↓ ${(info.download_bytes / 1024 / 1024).toFixed(2)} MB | ↑ ${(info.upload_bytes / 1024 / 1024).toFixed(2)} MB`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* Acciones rápidas */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Acciones
                            </Typography>

                            {ticket.status === 'open' && (
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    startIcon={<PlayArrow />}
                                    onClick={() => updateTicketStatus('in_progress')}
                                    sx={{ mb: 1 }}
                                >
                                    Tomar Ticket
                                </Button>
                            )}

                            {ticket.status === 'in_progress' && (
                                <>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<Message />}
                                        onClick={() => setResponseDialog(true)}
                                        sx={{ mb: 1 }}
                                    >
                                        Agregar Respuesta
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="success"
                                        startIcon={<CheckCircle />}
                                        onClick={() => setResolveDialog(true)}
                                    >
                                        Resolver Ticket
                                    </Button>
                                </>
                            )}

                            {ticket.status === 'closed' && (
                                <Alert severity="success">
                                    Este ticket ha sido resuelto
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Dialog para agregar respuesta */}
            <Dialog open={responseDialog} onClose={() => setResponseDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Agregar Respuesta</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        multiline
                        rows={4}
                        fullWidth
                        label="Tu respuesta"
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        placeholder="Describe las acciones realizadas o la información proporcionada al cliente..."
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResponseDialog(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={addResponse}
                        variant="contained"
                        disabled={!responseText.trim() || submitting}
                        startIcon={submitting ? <Refresh /> : <Send />}
                    >
                        {submitting ? 'Enviando...' : 'Enviar Respuesta'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog para resolver ticket */}
            <Dialog open={resolveDialog} onClose={() => setResolveDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Resolver Ticket</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        multiline
                        rows={4}
                        fullWidth
                        label="Descripción de la resolución"
                        value={resolutionText}
                        onChange={(e) => setResolutionText(e.target.value)}
                        placeholder="Describe cómo se resolvió el problema y las acciones tomadas..."
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResolveDialog(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={resolveTicket}
                        variant="contained"
                        color="success"
                        disabled={!resolutionText.trim() || submitting}
                        startIcon={submitting ? <Refresh /> : <CheckCircle />}
                    >
                        {submitting ? 'Resolviendo...' : 'Resolver Ticket'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* FAB para acciones rápidas */}
            {ticket.status !== 'closed' && (
                <Fab
                    color="primary"
                    sx={{ position: 'fixed', bottom: 16, right: 16 }}
                    onClick={() => setResponseDialog(true)}
                >
                    <Message />
                </Fab>
            )}
        </Box>
    );
};

export default TicketDetails;